import { useState, useEffect } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { useAppStore } from '../stores/app'

interface AuthState {
  user: User | null
  session: Session | null
  loading: boolean
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    loading: true,
  })
  const setCurrentUser = useAppStore((state) => state.setCurrentUser)

  useEffect(() => {
    // Obtener sesión inicial
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setState({
        user: session?.user ?? null,
        session,
        loading: false,
      })
      
      // Cargar perfil de usuario si existe sesión
      if (session?.user) {
        try {
          const { data: profile, error } = await supabase
            .from('user_profiles')
            .select('*')
            .eq('id', session.user.id)
            .single()

          if (profile) {
            setCurrentUser(profile)
          } else if (error) {
            // Profile not yet created (trigger race condition after signup)
            setCurrentUser({
              id: session.user.id,
              email: session.user.email ?? null,
              organization_id: null,
              full_name: null,
              role: null,
            })
          }
        } catch {
          setCurrentUser({
            id: session.user.id,
            email: session.user.email ?? null,
            organization_id: null,
            full_name: null,
            role: null,
          })
        }
      }
    })

    // Escuchar cambios de auth
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setState({
        user: session?.user ?? null,
        session,
        loading: false,
      })
      
      // Skip profile loading here - it causes the callback to hang
      // Profile will be loaded by useOrganization hook when needed
      if (!session?.user) {
        setCurrentUser(null)
      }
    })

    return () => subscription.unsubscribe()
  }, [setCurrentUser])

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    if (error) throw error
    return data
  }

  const signUp = async (email: string, password: string, nombreCompleto: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: nombreCompleto },
      },
    })
    if (error) throw error
    return data
  }

  const signOut = async () => {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
  }

  const refreshProfile = async () => {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.user) return
    const { data: profile } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', session.user.id)
      .single()
    if (profile) {
      setCurrentUser(profile)
    }
  }

  return {
    ...state,
    signIn,
    signUp,
    signOut,
    refreshProfile,
  }
}
